/**
 * =========================================================
 * TAZTIR 凑单神器（In-Cart Upsells）- 竖向卡片 + 状态动画版
 * ---------------------------------------------------------
 * 你当前线上版本：队列版（Promise Queue）✅
 * 本次优化目标：
 * 1) 按钮不再通过“改文字”来显示 Adding/Retrying（避免溢出、避免卡片抖动）
 * 2) 统一用 data-state 驱动按钮状态（由 CSS 控制显示与动画）
 * 3) 保留原有的抗 429：Retry-After 优先 + 指数退避 + 串行队列
 *
 * 按钮状态约定（与 Liquid/CSS 对应）：
 * - idle     ：显示 Add
 * - queued   ：显示 Queued
 * - adding   ：显示 Adding
 * - retrying ：显示 Retrying
 * - added    ：显示 Added（短暂显示后回 idle）
 *
 * 抽屉刷新：
 * - Section Rendering API 固定请求 "/"，跨页面稳定
 * - 只 patch .cart-drawer__items 与 .cart-drawer__summary
 * =========================================================
 */

/** ---------------------------
 * 可调配置（建议先用默认值）
 * -------------------------- */
const TAZTIR_CFG = {
  // 429 最大重试次数（不含第一次）
  maxRetries: 4,

  // 没有 Retry-After 时的指数退避基数（ms）
  backoffBase: 1000,

  // 指数退避最大等待（ms）
  backoffMax: 8000,

  // 成功态 “Added” 停留时长（ms）
  addedFlashDuration: 1000,

  // 每次成功后额外冷却（ms），进一步降低触发 429 概率
  cooldownAfterSuccess: 500,

  // 队列过长提示阈值（用户狂点时给轻提示）
  queueWarnThreshold: 3,

  // retrying 时 toast 最长显示时长（ms）
  toastMaxDuration: 1600,
};

/** ---------------------------
 * Promise 队列：确保所有 add 串行执行，避免并发导致限流/状态错乱
 * -------------------------- */
let taztirQueue = Promise.resolve();
/** 当前队列长度（用于提示“排队中”） */
let taztirQueueLen = 0;

/** ---------------------------
 * 工具：sleep
 * -------------------------- */
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** ---------------------------
 * 工具：fetch（返回 Response，便于读取响应头 Retry-After）
 * -------------------------- */
async function taztirFetch(url, options = {}) {
  return fetch(url, {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });
}

/** =========================================================
 * Toast：轻量提示（不依赖主题）
 * - 只在 retrying/队列过长/失败时提示
 * ========================================================= */
function ensureToast() {
  let el = document.querySelector('[data-taztir-toast]');
  if (el) return el;

  el = document.createElement('div');
  el.setAttribute('data-taztir-toast', 'true');
  el.style.cssText = `
    position: fixed;
    right: 18px;
    bottom: 18px;
    z-index: 99999;
    max-width: 300px;
    padding: 10px 12px;
    border-radius: 12px;
    border: 1px solid rgba(0,0,0,0.08);
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(8px);
    color: #141414;
    font-size: 12px;
    line-height: 1.35;
    box-shadow: 0 10px 30px rgba(0,0,0,0.10);
    opacity: 0;
    transform: translateY(8px);
    transition: opacity .18s ease, transform .18s ease;
    pointer-events: none;
  `;
  document.body.appendChild(el);
  return el;
}

let toastTimer = null;
function toast(msg, duration = 1300) {
  const el = ensureToast();
  el.textContent = msg;
  el.style.opacity = '1';
  el.style.transform = 'translateY(0)';

  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    el.style.opacity = '0';
    el.style.transform = 'translateY(8px)';
  }, duration);
}

/** =========================================================
 * 按钮状态：只改 data-state（不改文字）
 * - 文字由 Liquid 输出的多个 span + CSS 叠层控制
 * ========================================================= */
function taztirSetBtnState(btn, state) {
  if (!btn) return;
  btn.setAttribute('data-state', state);
}

/**
 * 在按钮仍存在于 DOM 时才设置状态
 * - 因为局部刷新会替换 .cart-drawer__items/.summary，旧按钮可能被移除
 */
function taztirSafeSetBtnState(btn, state) {
  if (!btn) return;
  if (!document.body.contains(btn)) return;
  taztirSetBtnState(btn, state);
}

/** =========================================================
 * 抽屉局部刷新（跨页面稳定）
 * ========================================================= */
function getDrawerDialog() {
  return (
    document.querySelector('.cart-drawer__dialog[open]') ||
    document.querySelector('.cart-drawer__dialog')
  );
}

function getDrawerSectionId() {
  const dialog = getDrawerDialog();
  return dialog?.getAttribute('data-cart-drawer-section-id');
}

function parseHTML(html) {
  return new DOMParser().parseFromString(html, 'text/html');
}

/**
 * 只 patch 抽屉关键区域，避免整块替换导致叠层/点击问题
 */
function patchDrawerPartial(newDoc) {
  const currentDialog = document.querySelector('.cart-drawer__dialog');
  const newDialog = newDoc.querySelector('.cart-drawer__dialog');
  if (!currentDialog || !newDialog) return;

  const currentItems = currentDialog.querySelector('.cart-drawer__items');
  const newItems = newDialog.querySelector('.cart-drawer__items');
  if (currentItems && newItems) currentItems.innerHTML = newItems.innerHTML;

  const currentSummary = currentDialog.querySelector('.cart-drawer__summary');
  const newSummary = newDialog.querySelector('.cart-drawer__summary');
  if (currentSummary && newSummary) currentSummary.innerHTML = newSummary.innerHTML;
}

async function rerenderDrawer() {
  const sectionId = getDrawerSectionId();
  if (!sectionId) {
    // 极端情况下无法局部刷新：兜底整页刷新
    window.location.reload();
    return;
  }

  // ✅ 固定从根路径拉取，保证 header/cart drawer 结构一致
  const url = `/?sections=${encodeURIComponent(sectionId)}`;

  const res = await fetch(url, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return;

  const data = await res.json().catch(() => null);
  if (!data || !data[sectionId]) return;

  const doc = parseHTML(data[sectionId]);
  patchDrawerPartial(doc);
}

/** =========================================================
 * 429 处理：优先读 Retry-After
 * ========================================================= */
function getRetryAfterMs(res, attempt) {
  const ra = res.headers.get('Retry-After');

  // 1) 秒数字（最常见）
  if (ra && /^[0-9]+(\.[0-9]+)?$/.test(ra.trim())) {
    const sec = Number(ra.trim());
    return Math.max(0, sec * 1000 + 200); // 加 200ms 缓冲
  }

  // 2) HTTP 日期格式（较少）
  if (ra) {
    const retryAt = Date.parse(ra);
    if (!Number.isNaN(retryAt)) {
      return Math.max(0, retryAt - Date.now() + 200);
    }
  }

  // 3) 没有 Retry-After：指数退避（上限 backoffMax）
  const ms = Math.min(
    TAZTIR_CFG.backoffMax,
    TAZTIR_CFG.backoffBase * Math.pow(2, attempt)
  );
  return ms;
}

/** =========================================================
 * 核心：带 429 处理与重试的 add
 * - 状态由 data-state 驱动
 * ========================================================= */
async function addToCartWith429Handling(variantId, btn) {
  for (let attempt = 0; attempt <= TAZTIR_CFG.maxRetries; attempt++) {
    const res = await taztirFetch('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
    });

    // 成功：返回 true
    if (res.ok) return true;

    // 429：等待后重试
    if (res.status === 429) {
      const waitMs = getRetryAfterMs(res, attempt);

      // 更新按钮状态为 retrying（不改变按钮尺寸）
      taztirSafeSetBtnState(btn, 'retrying');

      // toast 提示等待（文案更克制现代，不用省略号）
      toast(`操作太频繁，${Math.ceil(waitMs / 1000)} 秒后重试`, Math.min(TAZTIR_CFG.toastMaxDuration, waitMs));

      await sleep(waitMs);
      continue;
    }

    // 其它错误（如 422）：不重试
    return false;
  }

  // 重试耗尽仍失败
  return false;
}

/** =========================================================
 * 队列：append 一个任务到队尾
 * - 通过 Promise 链保证串行
 * - 防止前一个任务 throw 造成队列断裂：catch 吞掉
 * ========================================================= */
function enqueue(task) {
  taztirQueueLen += 1;

  // 队列过长时给轻提示（避免用户误以为没点上）
  if (taztirQueueLen >= TAZTIR_CFG.queueWarnThreshold) {
    toast('正在排队添加，请稍等', 1200);
  }

  taztirQueue = taztirQueue
    .catch(() => {
      // 防止队列链断掉：吞掉上一个任务的异常
    })
    .then(async () => {
      try {
        await task();
      } finally {
        taztirQueueLen = Math.max(0, taztirQueueLen - 1);
      }
    });

  return taztirQueue;
}

/** =========================================================
 * 点击事件代理：处理 upsell Add
 * ========================================================= */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-taztir-add]');
  if (!btn) return;

  // 不可用（售罄/禁用）直接返回
  if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

  const variantId = btn.getAttribute('data-variant-id');
  if (!variantId) return;

  /**
   * 点击后立即显示 queued：
   * - 让用户马上感知“已响应”
   * - 也避免用户连点导致更多请求
   */
  taztirSetBtnState(btn, 'queued');

  enqueue(async () => {
    // 轮到执行：adding
    taztirSafeSetBtnState(btn, 'adding');

    const ok = await addToCartWith429Handling(variantId, btn);

    if (!ok) {
      // 失败：回 idle + toast
      taztirSafeSetBtnState(btn, 'idle');
      toast('添加失败，请稍后再试', 1400);
      return;
    }

    // 成功：added（短暂停留后回 idle）
    taztirSafeSetBtnState(btn, 'added');

    // 刷新抽屉（更新商品/价格/免邮进度条/凑单列表等）
    await rerenderDrawer();

    // Added 停留一会再回 idle（注意：局部刷新后原按钮可能不在 DOM）
    await sleep(TAZTIR_CFG.addedFlashDuration);
    taztirSafeSetBtnState(btn, 'idle');

    // 成功后冷却，降低连续操作触发 429 的概率
    await sleep(TAZTIR_CFG.cooldownAfterSuccess);
  });
});